// Motor de cálculo de reportes.
import type {
  SeedEmployee,
  SeedTimeEntry,
  SeedAbsence,
  SeedAdminDay,
  EmployeeReport,
  AreaReport,
} from '../data/types';
import { JORNADA_HORAS } from '../data/types';

// ┌──────────────────────────────────────────────────────────────────────┐
// │  FÓRMULA DE CUMPLIMIENTO — EDITAR ACÁ                                  │
// │                                                                        │
// │  META = (días hábiles − ausencias) × horas/día según jornada          │
// │    - ausencias = días administrativos + licencias + vacaciones        │
// │    - horas/día: Full-Time 7 · Part-Time 4  (ver JORNADA_HORAS)         │
// │                                                                        │
// │  La META es el MISMO denominador para ambos cumplimientos:            │
// │    Cumplimiento Total     = horas trabajadas (todas)    / META         │
// │    Cumplimiento Facturado = horas facturables (billable)/ META         │
// │                                                                        │
// │  "Cumple" = % ≥ UMBRAL_CUMPLE (100%)                                   │
// └──────────────────────────────────────────────────────────────────────┘
export const UMBRAL_CUMPLE = 1.0; // % a partir del cual un empleado "Cumple" (1.0 = 100%)

export interface ReportFilters {
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
}

export interface ReportData {
  employees: SeedEmployee[];
  timeEntries: SeedTimeEntry[];
  absences: SeedAbsence[];
  adminDays: SeedAdminDay[];
  // Días no laborales que aplican a TODA la empresa (feriados + actividades oficina).
  // Cada uno descuenta de la meta: 'completo' = 1 día, 'media_jornada' = 0.5 día.
  // Opcional para no romper llamadas existentes; si falta, no descuenta nada extra.
  nonWorkingDays?: NonWorkingDay[];
}

export interface NonWorkingDay {
  date: string; // YYYY-MM-DD
  portion: 'completo' | 'media_jornada';
}

// Parsea "YYYY-MM-DD" como medianoche LOCAL (no UTC). Sin el T00:00:00 el
// navegador lo interpreta como UTC y, en husos negativos (Chile), getDay()
// retrocede un día → se perdía un día hábil al contar la meta.
function parseDate(d: string): Date {
  return new Date(d + 'T00:00:00');
}

// Lista de fechas hábiles (lun-vie) entre start y end, ambas inclusive, como
// strings YYYY-MM-DD. Base para calcular la meta día por día sin doble descuento.
function weekdaysBetween(startDate: string, endDate: string): string[] {
  const days: string[] = [];
  const current = parseDate(startDate);
  const end = parseDate(endDate);
  while (current <= end) {
    const dow = current.getDay();
    if (dow !== 0 && dow !== 6) {
      days.push(
        `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(
          current.getDate()
        ).padStart(2, '0')}`
      );
    }
    current.setDate(current.getDate() + 1);
  }
  return days;
}

export function countWeekdays(start: Date, end: Date): number {
  let count = 0;
  const current = new Date(start);
  while (current <= end) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) count++;
    current.setDate(current.getDate() + 1);
  }
  return count;
}

export function countWeekdaysInRange(
  rangeStart: string,
  rangeEnd: string,
  periodStart: string,
  periodEnd: string
): number {
  const start = new Date(
    Math.max(parseDate(rangeStart).getTime(), parseDate(periodStart).getTime())
  );
  const end = new Date(
    Math.min(parseDate(rangeEnd).getTime(), parseDate(periodEnd).getTime())
  );
  if (start > end) return 0;
  return countWeekdays(start, end);
}

export function generateReport(
  data: ReportData,
  filters: ReportFilters
): AreaReport[] {
  const { startDate, endDate } = filters;
  const { employees, timeEntries, absences, adminDays } = data;

  const activeEmployees = employees.filter((e) => e.isActive);

  const entries = timeEntries.filter(
    (e) => e.date >= startDate && e.date <= endDate
  );

  const periodAbsences = absences.filter(
    (a) => a.startDate <= endDate && a.endDate >= startDate
  );

  const periodAdminDays = adminDays.filter(
    (d) => d.date >= startDate && d.date <= endDate
  );

  // Lista de días hábiles (lun-vie) del período, una sola vez.
  const weekdayList = weekdaysBetween(startDate, endDate);

  // Fracción NO trabajable de cada día por días no laborales de empresa
  // (feriado / actividad completa = 1 · media jornada = 0.5). Mapa fecha → fracción.
  const companyNonWorking = new Map<string, number>();
  for (const nw of data.nonWorkingDays ?? []) {
    const frac = nw.portion === 'media_jornada' ? 0.5 : 1;
    // si hay varios el mismo día, gana el mayor descuento
    companyNonWorking.set(nw.date, Math.max(companyNonWorking.get(nw.date) ?? 0, frac));
  }

  const employeeReports = new Map<string, EmployeeReport>();

  for (const emp of activeEmployees) {
    const targetBillablePct = emp.targetBillablePct ?? 0.75;

    const empEntries = entries.filter((e) => e.employeeId === emp.id);
    const actualTotalHours = empEntries.reduce(
      (sum, e) => sum + e.durationHours,
      0
    );
    const actualBillableHours = empEntries
      .filter((e) => e.billable)
      .reduce((sum, e) => sum + e.durationHours, 0);

    // Valor UF facturado: suma del Valor Trabajo (solo registros facturables, en UF).
    const valueUF = empEntries
      .filter((e) => e.billable && e.currency === 'UF')
      .reduce((sum, e) => sum + e.value, 0);

    const empAbsences = periodAbsences.filter((a) => a.employeeId === emp.id);
    const empAdminDays = periodAdminDays.filter((d) => d.employeeId === emp.id);
    // Fracción NO trabajable por día administrativo (0.5 o 1) del colaborador.
    const adminByDate = new Map<string, number>();
    for (const d of empAdminDays) {
      adminByDate.set(d.date, Math.min(1, (adminByDate.get(d.date) ?? 0) + d.quantity));
    }

    // Días trabajables: recorro cada día hábil y tomo la fracción trabajable
    // (1 − mayor descuento del día). Así un día con varias razones (vacación +
    // feriado, etc.) se descuenta UNA sola vez, nunca doble.
    let workableDays = 0;
    for (const day of weekdayList) {
      const inAbsence = empAbsences.some(
        (a) => day >= a.startDate && day <= a.endDate
      )
        ? 1
        : 0;
      const adminFrac = adminByDate.get(day) ?? 0;
      const companyFrac = companyNonWorking.get(day) ?? 0;
      // El día se descuenta según la razón MÁS fuerte que lo cubra (máximo).
      const deduction = Math.max(inAbsence, adminFrac, companyFrac);
      workableDays += 1 - deduction;
    }

    // META única según jornada (Full-Time 7 / Part-Time 4 hrs por día hábil).
    const horasJornada = JORNADA_HORAS[emp.jornada ?? 'full_time'];
    const meta = workableDays * horasJornada;

    // La misma meta es denominador de ambos cumplimientos (ver bloque FÓRMULA).
    const expectedTotalHours = meta;
    const expectedBillableHours = meta;

    // Total usa TODAS las horas trabajadas; Facturado solo las facturables.
    const pctTotal = meta > 0 ? actualTotalHours / meta : 0;
    const pctBillable = meta > 0 ? actualBillableHours / meta : 0;

    employeeReports.set(emp.id, {
      employeeId: emp.id,
      displayName: emp.displayName,
      legalName: emp.legalName,
      rut: emp.rut || null,
      area: emp.area,
      subArea: emp.area, // por defecto el área real; útil para la card Procuradores
      role: emp.role || 'Empleado',
      company: emp.company,
      targetBillablePct,
      expectedTotalHours,
      expectedBillableHours,
      actualTotalHours,
      actualBillableHours,
      valueUF,
      pctTotal,
      pctBillable,
    });
  }

  const areaMap = new Map<string, AreaReport>();

  function getOrCreateArea(area: string): AreaReport {
    if (!areaMap.has(area)) {
      areaMap.set(area, {
        area,
        employees: [],
        expectedTotalHours: 0,
        expectedBillableHours: 0,
        actualTotalHours: 0,
        actualBillableHours: 0,
        valueUF: 0,
        pctTotal: 0,
        pctBillable: 0,
        cumpleTotalPct: 0,
        cumpleBillablePct: 0,
      });
    }
    return areaMap.get(area)!;
  }

  for (const report of employeeReports.values()) {
    // "Sin área" no se muestra en el reporte.
    if (!report.area) continue;

    // Los procuradores se muestran en su propia card, sub-agrupados por su área real.
    const targetCard =
      report.role === 'Procurador' ? PROCURADORES_CARD : report.area;

    const areaReport = getOrCreateArea(targetCard);
    areaReport.employees.push(report);
    areaReport.expectedTotalHours += report.expectedTotalHours;
    areaReport.expectedBillableHours += report.expectedBillableHours;
    areaReport.actualTotalHours += report.actualTotalHours;
    areaReport.actualBillableHours += report.actualBillableHours;
    areaReport.valueUF += report.valueUF;
  }

  for (const areaReport of areaMap.values()) {
    areaReport.pctTotal =
      areaReport.expectedTotalHours > 0
        ? areaReport.actualTotalHours / areaReport.expectedTotalHours
        : 0;
    areaReport.pctBillable =
      areaReport.expectedBillableHours > 0
        ? areaReport.actualBillableHours / areaReport.expectedBillableHours
        : 0;

    // Header Cumple/No Cumple: % de personas que llegan al umbral (Total y Fact por separado).
    const n = areaReport.employees.length;
    if (n > 0) {
      const cumpleTotal = areaReport.employees.filter(
        (e) => e.pctTotal >= UMBRAL_CUMPLE
      ).length;
      const cumpleBill = areaReport.employees.filter(
        (e) => e.pctBillable >= UMBRAL_CUMPLE
      ).length;
      areaReport.cumpleTotalPct = cumpleTotal / n;
      areaReport.cumpleBillablePct = cumpleBill / n;
    }

    // En la card Procuradores ordenamos por sub-área y luego por nombre;
    // en el resto, solo por nombre.
    if (areaReport.area === PROCURADORES_CARD) {
      areaReport.employees.sort(
        (a, b) =>
          (a.subArea || '').localeCompare(b.subArea || '') ||
          a.displayName.localeCompare(b.displayName)
      );
    } else {
      areaReport.employees.sort((a, b) =>
        a.displayName.localeCompare(b.displayName)
      );
    }
  }

  // Orden de las cards: Unitax, Corporativo, Tributarios, Procuradores, luego el resto.
  return Array.from(areaMap.values()).sort(
    (a, b) => cardOrder(a.area) - cardOrder(b.area) || a.area.localeCompare(b.area)
  );
}

const PROCURADORES_CARD = 'Procuradores';

// Orden fijo de las cards en el reporte.
const CARD_ORDER = ['Unitax', 'Corporativo', 'Tributarios', PROCURADORES_CARD];
function cardOrder(area: string): number {
  const i = CARD_ORDER.indexOf(area);
  return i === -1 ? CARD_ORDER.length : i;
}
